import { useSearch } from "@tanstack/react-router";
import { Button } from "antd";
import { useGetIntegrationCustomFieldQuery } from "api/TenantSlug/IntegrationCustomField/hooks";
import { AddApiToolCustomFieldForm } from "components/Forms/AddApiToolCustomFieldForm/AddApiToolCustomFieldForm";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { DrawerId, FormId } from "types/models.ts";

interface AddApiToolCustomFieldsDrawerProps {
    integrationId: string;
    onSubmit: () => void;
}

export const AddApiToolCustomFieldDrawer: FC<AddApiToolCustomFieldsDrawerProps> = ({ integrationId, onSubmit }) => {
    const { t } = usePaldockTranslation("drawers.add-api-tool-custom-field");
    const { t: tC } = usePaldockTranslation("common");
    const id = DrawerId.AddApiToolCustomField;
    const drawerUtils = useDrawer();
    const search = useSearch({ strict: false }) as { customFieldId?: string };

    const getIntegrationCustomFieldQuery = useGetIntegrationCustomFieldQuery(
        integrationId,
        search.customFieldId ?? "",
        {
            enabled: !!search.customFieldId,
        }
    );

    const title = search.customFieldId
        ? t("title_edit", { parameter: getIntegrationCustomFieldQuery.data?.integrationCustomField.parameter })
        : t("title_create");

    const handleSubmit = async () => {
        onSubmit?.();
        drawerUtils.hide(id, ["customFieldId"]);
    };

    return (
        <Drawer
            drawerId={id}
            title={<DrawerTitle mainTitle={title} />}
            searchParamsToClean={["customFieldId"]}
            extraButtons={
                <Button size={"large"} htmlType="submit" type={"primary"} form={FormId.AddApiToolCustomField}>
                    {tC("save")}
                </Button>
            }
            destroyOnClose
        >
            <DrawerContent>
                <DrawerCollapsible title={t("section_title")}>
                    <AddApiToolCustomFieldForm
                        integrationId={integrationId}
                        customFieldId={search.customFieldId}
                        onSubmit={handleSubmit}
                    />
                </DrawerCollapsible>
            </DrawerContent>
        </Drawer>
    );
};
